import {
  ChevronRight,
  CreditCard,
  Rocket,
  Sparkles,
  Wrench,
  Settings,
  LayoutDashboard,
  Bot,
  Users,
  User,
  Target,
  FileBarChart,
  FileOutput,
  FileUser,
  Shield,
  Link as LinkIcon,
  Building2,
  BarChart3,
  UserCog,
  UsersRound,
  BookOpen,
  HelpCircle,
  PlayCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { Category, SubCategory } from '../../content/registry';

import { ArticleCard } from './ArticleCard';

const iconMap: Record<string, LucideIcon> = {
  Rocket,
  Sparkles,
  Wrench,
  Settings,
  LayoutDashboard,
  Bot,
  Users,
  User,
  Target,
  FileBarChart,
  FileOutput,
  FileUser,
  Shield,
  CreditCard,
  Link: LinkIcon,
  Building2,
  BarChart3,
  UserCog,
  UsersRound,
  BookOpen,
  HelpCircle,
  PlayCircle,
};

/** Threshold for considering a subcategory as "large" (renders full-width with 2-column list) */
const LARGE_SUBCATEGORY_THRESHOLD = 4;

interface CategorySectionProps {
  category: Category;
  fromRole?: string;
}

export function CategorySection({ category, fromRole }: CategorySectionProps) {
  const CategoryIcon = iconMap[category.icon];

  return (
    <section className='mb-10'>
      {/* Category heading */}
      <div className='mb-4 flex items-center gap-2.5'>
        <div className='flex h-6 w-6 items-center justify-center rounded bg-primary'>
          {CategoryIcon && <CategoryIcon size={14} className='text-white' />}
        </div>
        <h2 className='text-lg font-bold text-text'>{category.title}</h2>
      </div>

      {/* Categories with direct articles (e.g., "はじめに", "トラブルシューティング") */}
      {category.articles && category.articles.length > 0 && (
        <div className='grid grid-cols-2 gap-3'>
          {category.articles.map((article) => (
            <ArticleCard
              key={article.id}
              title={article.title}
              path={article.path}
              fromRole={fromRole}
            />
          ))}
        </div>
      )}

      {/* Categories with subcategories (e.g., "TONOME Coreの機能", "管理者向け") */}
      {category.subcategories && category.subcategories.length > 0 && (
        <SubCategoryGrid subcategories={category.subcategories} fromRole={fromRole} />
      )}
    </section>
  );
}

interface SubCategoryGridProps {
  subcategories: SubCategory[];
  fromRole?: string;
}

function SubCategoryGrid({ subcategories, fromRole }: SubCategoryGridProps) {
  // Render subcategories in original order, grouping small ones in 2-col grids
  // and large ones as full-width cards
  const elements: React.ReactNode[] = [];
  let pendingSmall: SubCategory[] = [];

  const flushSmall = () => {
    if (pendingSmall.length > 0) {
      elements.push(
        <div
          key={`grid-${pendingSmall[0]?.id}`}
          className={`grid grid-cols-2 gap-3 ${elements.length > 0 ? 'mt-3' : ''}`}
        >
          {pendingSmall.map((sub) => (
            <SubCategoryCard
              key={sub.id}
              title={sub.title}
              icon={sub.icon}
              articles={sub.articles.map((a) => ({ title: a.title, path: a.path }))}
              fromRole={fromRole}
            />
          ))}
        </div>
      );
      pendingSmall = [];
    }
  };

  for (const sub of subcategories) {
    if (sub.articles.length > LARGE_SUBCATEGORY_THRESHOLD) {
      // Flush any pending small subcategories first
      flushSmall();
      elements.push(
        <div key={sub.id} className={elements.length > 0 ? 'mt-3' : ''}>
          <SubCategoryCard
            title={sub.title}
            icon={sub.icon}
            articles={sub.articles.map((a) => ({ title: a.title, path: a.path }))}
            fullWidth
            fromRole={fromRole}
          />
        </div>
      );
    } else {
      pendingSmall.push(sub);
      // Flush when we have a pair
      if (pendingSmall.length === 2) {
        flushSmall();
      }
    }
  }
  // Flush remaining
  flushSmall();

  return <>{elements}</>;
}

interface SubCategoryCardProps {
  title: string;
  icon: string;
  articles: { title: string; path: string }[];
  fullWidth?: boolean;
  fromRole?: string;
}

function SubCategoryCard({ title, icon, articles, fullWidth, fromRole }: SubCategoryCardProps) {
  const SubIcon = iconMap[icon];

  return (
    <div className='rounded-lg border border-border bg-white p-5'>
      {/* Subcategory header */}
      <div className='mb-3 flex items-center gap-2'>
        {SubIcon && <SubIcon size={18} className='text-text' />}
        <h3 className='text-sm font-bold text-text'>{title}</h3>
      </div>

      {/* Divider + article links */}
      <div
        className={`border-t border-border pt-3 ${
          fullWidth ? 'grid grid-cols-2 gap-x-6 gap-y-1' : 'space-y-1'
        }`}
      >
        {articles.map((article) => (
          <Link
            key={article.path}
            to={fromRole ? `${article.path}?from=${fromRole}` : article.path}
            className='group flex items-center gap-2 py-1.5 text-sm text-text transition-colors hover:text-primary'
          >
            <ChevronRight
              size={14}
              className='shrink-0 text-[#C0C0C0] transition-colors group-hover:text-primary'
            />
            {article.title}
          </Link>
        ))}
      </div>
    </div>
  );
}
